import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { FileText, Download, Mail } from "lucide-react";
import { Advertisement } from "@/types/advertisement";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

// Company Information
const COMPANY_ABN = "61 168 348 463";

interface AdInvoiceGeneratorProps {
  ad: Advertisement;
}

export function AdInvoiceGenerator({ ad }: AdInvoiceGeneratorProps) {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [invoiceData, setInvoiceData] = useState({
    invoiceNumber: `INV-${Date.now()}`,
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    clientEmail: ad.email || ad.contact,
    notes: "",
  });

  const views = parseInt(ad.views || "0");
  const clicks = parseInt(ad.clicks || "0");
  const basePrice = parseFloat(ad.price || "0");
  
  // Calculate pricing based on plan
  let amount = basePrice;
  let description = "";
  
  if (ad.plan === "cpc") {
    amount = clicks * basePrice;
    description = `${clicks} clicks × AUD $${basePrice} per click`;
  } else if (ad.plan === "cpm") {
    const impressions = Math.floor(views / 1000);
    amount = impressions * basePrice;
    description = `${impressions} thousand impressions × AUD $${basePrice} per thousand`;
  } else {
    description = `Fixed rate advertising campaign - Prepaid`;
    amount = basePrice;
  }

  const tax = amount * 0.1; // 10% GST
  const total = amount + tax;

  const generateInvoiceHTML = () => {
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    body { font-family: Arial, sans-serif; max-width: 800px; margin: 40px auto; padding: 20px; }
    .header { border-bottom: 3px solid #333; padding-bottom: 20px; margin-bottom: 30px; }
    .company-name { font-size: 28px; font-weight: bold; color: #333; }
    .invoice-title { font-size: 24px; margin: 20px 0; }
    .info-section { margin: 20px 0; }
    .info-row { display: flex; justify-content: space-between; margin: 10px 0; }
    table { width: 100%; border-collapse: collapse; margin: 30px 0; }
    th, td { padding: 12px; text-align: left; border-bottom: 1px solid #ddd; }
    th { background-color: #f5f5f5; font-weight: bold; }
    .total-row { font-weight: bold; font-size: 18px; }
    .notes { margin-top: 30px; padding: 15px; background-color: #f9f9f9; border-left: 4px solid #333; }
    .footer { margin-top: 50px; text-align: center; color: #666; font-size: 12px; }
  </style>
</head>
<body>
  <div class="header">
    <div class="company-name">OZK News</div>
    <div>ABN: ${COMPANY_ABN}</div>
  </div>

  <h1 class="invoice-title">INVOICE</h1>

  <div class="info-section">
    <div class="info-row">
      <div><strong>Invoice Number:</strong> ${invoiceData.invoiceNumber}</div>
      <div><strong>Issue Date:</strong> ${invoiceData.issueDate}</div>
    </div>
    <div class="info-row">
      <div><strong>Due Date:</strong> ${invoiceData.dueDate}</div>
    </div>
  </div>

  <div class="info-section">
    <h3>Bill To:</h3>
    <div><strong>${ad.advertiser}</strong></div>
    ${ad.businessAddress ? `<div>${ad.businessAddress}</div>` : ''}
    ${ad.businessCity || ad.businessState || ad.businessPostcode ? `<div>${ad.businessCity || ''}${ad.businessCity && (ad.businessState || ad.businessPostcode) ? ', ' : ''}${ad.businessState || ''} ${ad.businessPostcode || ''}</div>` : ''}
    <div>Phone: ${ad.contact}</div>
    ${ad.email ? `<div>Email: ${ad.email}</div>` : ''}
  </div>

  <table>
    <thead>
      <tr>
        <th>Description</th>
        <th>Period</th>
        <th>Quantity</th>
        <th>Unit Price</th>
        <th>Amount</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>
          <strong>${ad.location.replace(/_/g, ' ').toUpperCase()} Advertisement</strong><br>
          ${description}
        </td>
        <td>${ad.startDate} to ${ad.endDate}</td>
        <td>${ad.plan === "cpc" ? clicks : ad.plan === "cpm" ? Math.floor(views / 1000) : 1}</td>
        <td>AUD $${basePrice.toFixed(2)}</td>
        <td>AUD $${amount.toFixed(2)}</td>
      </tr>
      <tr>
        <td colspan="4" style="text-align: right;"><strong>Subtotal:</strong></td>
        <td>AUD $${amount.toFixed(2)}</td>
      </tr>
      <tr>
        <td colspan="4" style="text-align: right;"><strong>GST (10%):</strong></td>
        <td>AUD $${tax.toFixed(2)}</td>
      </tr>
      <tr class="total-row">
        <td colspan="4" style="text-align: right;">TOTAL DUE:</td>
        <td>AUD $${total.toFixed(2)}</td>
      </tr>
    </tbody>
  </table>

  ${ad.plan !== "fixed" ? `
  <div><strong>Campaign Performance:</strong></div>
  <ul>
    <li>Total Views: ${views.toLocaleString()}</li>
    <li>Total Clicks: ${clicks.toLocaleString()}</li>
    <li>Click-Through Rate (CTR): ${views > 0 ? ((clicks / views) * 100).toFixed(2) : 0}%</li>
  </ul>
  ` : ''}

  ${invoiceData.notes ? `
  <div class="notes">
    <strong>Notes:</strong><br>
    ${invoiceData.notes.replace(/\n/g, '<br>')}
  </div>
  ` : ''}

  <div class="footer">
    <p><strong>Thank you for your business!</strong></p>
    <p style="margin: 15px 0;"><strong>Payment Terms:</strong> Due within 30 days</p>
    <p style="margin: 15px 0;">
      <strong>Bank Details:</strong><br>
      Account Name: HAGYUN KANG<br>
      BSB: 062271<br>
      Account Number: 10294202
    </p>
    <p>For any queries, please contact: <a href="mailto:ozknews.com@gmail.com">ozknews.com@gmail.com</a></p>
  </div>
</body>
</html>
    `;
  };

  const downloadInvoice = () => {
    const htmlContent = generateInvoiceHTML();
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${invoiceData.invoiceNumber}_${ad.advertiser.replace(/\s/g, '_')}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast({
      title: "Success",
      description: "Invoice downloaded successfully!",
    });
  };

  const sendInvoiceEmail = async () => {
    try {
      if (!invoiceData.clientEmail) {
        toast({
          title: "Error",
          description: "Please enter a client email address",
          variant: "destructive",
        });
        return;
      }

      const htmlContent = generateInvoiceHTML();
      
      await apiRequest("POST", "/api/send-invoice", {
        to: invoiceData.clientEmail,
        subject: `Invoice ${invoiceData.invoiceNumber} - OZK News`,
        html: htmlContent,
      });

      toast({
        title: "Success",
        description: `Invoice sent successfully to ${invoiceData.clientEmail}`,
      });
      
      setIsOpen(false);
    } catch (error) {
      console.error("Error sending invoice:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to send invoice email",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid={`button-invoice-${ad.id}`}>
          <FileText className="h-4 w-4 mr-2" />
          Generate Invoice
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Generate Invoice - {ad.advertiser}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="invoiceNumber">Invoice Number</Label>
              <Input
                id="invoiceNumber"
                value={invoiceData.invoiceNumber}
                onChange={(e) => setInvoiceData({ ...invoiceData, invoiceNumber: e.target.value })}
                data-testid="input-invoice-number"
              />
            </div>
            <div>
              <Label htmlFor="clientEmail">Client Email</Label>
              <Input
                id="clientEmail"
                type="email"
                value={invoiceData.clientEmail}
                onChange={(e) => setInvoiceData({ ...invoiceData, clientEmail: e.target.value })}
                data-testid="input-client-email"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="issueDate">Issue Date</Label>
              <Input
                id="issueDate"
                type="date"
                value={invoiceData.issueDate}
                onChange={(e) => setInvoiceData({ ...invoiceData, issueDate: e.target.value })}
                data-testid="input-issue-date"
              />
            </div>
            <div>
              <Label htmlFor="dueDate">Due Date</Label>
              <Input
                id="dueDate"
                type="date"
                value={invoiceData.dueDate}
                onChange={(e) => setInvoiceData({ ...invoiceData, dueDate: e.target.value })}
                data-testid="input-due-date"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="notes">Additional Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={invoiceData.notes}
              onChange={(e) => setInvoiceData({ ...invoiceData, notes: e.target.value })}
              placeholder="Enter any additional notes or payment instructions..."
              rows={3}
              data-testid="input-invoice-notes"
            />
          </div>

          <Card className="bg-muted/50">
            <CardHeader>
              <CardTitle className="text-lg">Invoice Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span>Campaign Period:</span>
                <span className="font-medium">{ad.startDate} to {ad.endDate}</span>
              </div>
              <div className="flex justify-between">
                <span>Views:</span>
                <span className="font-medium">{views.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>Clicks:</span>
                <span className="font-medium">{clicks.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>CTR:</span>
                <span className="font-medium">{views > 0 ? ((clicks / views) * 100).toFixed(2) : 0}%</span>
              </div>
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span className="font-medium">AUD ${amount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>GST (10%):</span>
                  <span>AUD ${tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold mt-2">
                  <span>Total:</span>
                  <span>AUD ${total.toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-2 pt-4">
            <Button onClick={downloadInvoice} className="flex-1" data-testid="button-download-invoice">
              <Download className="h-4 w-4 mr-2" />
              Download Invoice
            </Button>
            <Button onClick={sendInvoiceEmail} variant="outline" className="flex-1" data-testid="button-email-invoice">
              <Mail className="h-4 w-4 mr-2" />
              Send via Email
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
